import { Currency } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import {
  CheckoutWidgetsBuilderState,
  DEFAULT_CHECKOUT_UPSELL_STYLING,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-widgets-builder";
import {
  CHECKOUT_IMAGE_BLOCK_DEFAULT_WIDTH_PX,
  CheckoutImageBlockJustification,
  CheckoutOptimizationWidgetType,
  CheckoutReviewsProvider,
  FAILED_DISCOUNT_RESCUE_WIDGET_DEFAULT_BUTTON_TEXT,
  FAILED_DISCOUNT_RESCUE_WIDGET_DEFAULT_TEXT,
  FULFILLMENT_CUTOFF_WIDGET_FASTEST_DELIVERY_DATE_PLACEHOLDER,
  FULFILLMENT_CUTOFF_WIDGET_ORDER_CUTOFF_TIME_PLACEHOLDER,
  ManualProductSourceType,
  PurchaseCheckoutBlockRenderTarget,
  SHIPPING_OPTION_IMAGE_DEFAULT_WIDTH,
  ShippingOptionRenderTarget,
  THRESHOLD_UPSELL_WIDGET_ADD_TO_CART_BUTTON_TEXT,
  THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER,
} from "@redotech/redo-conversion-core-common/checkouts/widget";
import { IntegrationKind } from "@redotech/redo-integrations-core";
import { ModalProduct } from "@redotech/redo-web/product/product-selection-modal";
import { createContext, memo, useState } from "react";
import type { Location } from "react-router-dom";
import { NavigateFunction } from "react-router-dom";

export interface CollectionSearchResult {
  id: string;
  name: string;
}

export function getDefaultWidgetByType(
  widgetType: CheckoutOptimizationWidgetType,
  currency: Currency,
  targetOverride?: string,
): CheckoutWidgetsBuilderState["checkoutWidgets"] {
  switch (widgetType) {
    case CheckoutOptimizationWidgetType.THRESHOLD_UPSELL:
      return {
        [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: {
          type: CheckoutOptimizationWidgetType.THRESHOLD_UPSELL,
          id: undefined,
          name: "Untitled widget",
          enabled: false,
          target:
            targetOverride ?? PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1,
          thresholdAmount: 100,
          currency,
          gteThresholdText: "You might also like",
          ltThresholdText: `Spend ${THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER} more to unlock free shipping`,
          addToCartButtonText: THRESHOLD_UPSELL_WIDGET_ADD_TO_CART_BUTTON_TEXT,
          addToCartButtonColor: "#141414",
          addToCartButtonTextColor: "#FFFFFF",
          progressBarColor: "#141414",
          excludedProductTags: [],
          fallbackProductTags: [],
          manualProductSources: [],
          styling: DEFAULT_CHECKOUT_UPSELL_STYLING,
        },
      };
    case CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL:
      return {
        [CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL]: {
          type: CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL,
          id: undefined,
          name: "Untitled widget",
          enabled: false,
          target:
            targetOverride ?? PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1,
          addToCartButtonColor: "#141414",
          addToCartButtonTextColor: "#FFFFFF",
          excludedProductTags: [],
          fallbackProductTags: [],
          manualProductSources: [{ type: ManualProductSourceType.REDO_AI }],
        },
      };
    case CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF:
      return {
        [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: {
          type: CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF,
          id: undefined,
          name: "Untitled widget",
          enabled: false,
          target: targetOverride ?? PurchaseCheckoutBlockRenderTarget.DELIVERY1,
          text: `Get it by ${FULFILLMENT_CUTOFF_WIDGET_FASTEST_DELIVERY_DATE_PLACEHOLDER}. Order by ${FULFILLMENT_CUTOFF_WIDGET_ORDER_CUTOFF_TIME_PLACEHOLDER}.`,
          backgroundColor: "#FFF8E0",
          iconColor: "#BE9E42",
        },
      };
    case CheckoutOptimizationWidgetType.CONTENT_BLOCK:
      return {
        [CheckoutOptimizationWidgetType.CONTENT_BLOCK]: {
          type: CheckoutOptimizationWidgetType.CONTENT_BLOCK,
          id: undefined,
          name: "Untitled widget",
          enabled: false,
          target:
            targetOverride ?? PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1,
          sections: [],
        },
      };
    case CheckoutOptimizationWidgetType.IMAGE_BLOCK:
      return {
        [CheckoutOptimizationWidgetType.IMAGE_BLOCK]: {
          type: CheckoutOptimizationWidgetType.IMAGE_BLOCK,
          id: undefined,
          name: "Untitled widget",
          enabled: false,
          target:
            targetOverride ?? PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1,
          imageUrl: undefined,
          imageWidthPx: String(CHECKOUT_IMAGE_BLOCK_DEFAULT_WIDTH_PX),
          imageJustification: CheckoutImageBlockJustification.LEFT,
        },
      };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT:
      return {
        [CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]: {
          type: CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT,
          id: undefined,
          name: "Untitled shipping widget",
          enabled: false,
          target: ShippingOptionRenderTarget.DETAILS_RENDER_AFTER,
          title: "",
          description: "",
          imageUrl: undefined,
          backgroundColor: "#FFFFFF00",
        },
      };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE:
      return {
        [CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE]: {
          type: CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE,
          id: undefined,
          name: "Untitled shipping image widget",
          enabled: false,
          target: ShippingOptionRenderTarget.DETAILS_RENDER_AFTER,
          imageUrl: undefined,
          imageWidth: SHIPPING_OPTION_IMAGE_DEFAULT_WIDTH,
          imageJustification: CheckoutImageBlockJustification.LEFT,
        },
      };
    case CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL:
      return {
        [CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL]: {
          type: CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL,
          id: undefined,
          name: "Untitled shipping upsell widget",
          enabled: false,
          // Drops down below the selected shipping method ("After the block").
          target: ShippingOptionRenderTarget.DETAILS_RENDER_EXPANDED,
          addToCartButtonText: THRESHOLD_UPSELL_WIDGET_ADD_TO_CART_BUTTON_TEXT,
          excludedProductTags: [],
          fallbackProductTags: [],
          manualProductSources: [{ type: ManualProductSourceType.REDO_AI }],
          // Progress bar off by default.
          progressBar: null,
        },
      };
    case CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE:
      return {
        [CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE]: {
          type: CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE,
          id: undefined,
          name: "Untitled widget",
          enabled: false,
          target:
            targetOverride ?? PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1,
          discountId: undefined,
          text: FAILED_DISCOUNT_RESCUE_WIDGET_DEFAULT_TEXT,
          buttonText: FAILED_DISCOUNT_RESCUE_WIDGET_DEFAULT_BUTTON_TEXT,
        },
      };
    case CheckoutOptimizationWidgetType.PRODUCT_REVIEWS:
      return {
        [CheckoutOptimizationWidgetType.PRODUCT_REVIEWS]: {
          type: CheckoutOptimizationWidgetType.PRODUCT_REVIEWS,
          id: undefined,
          name: "Untitled widget",
          enabled: false,
          target:
            targetOverride ?? PurchaseCheckoutBlockRenderTarget.ORDER_SUMMARY1,
          provider: CheckoutReviewsProvider.JUDGE_ME,
          minRating: 5,
          display: {
            showProductName: true,
            showReviewTitle: true,
            showReviewBody: true,
            showReviewerName: true,
            showVerifiedBadge: false,
          },
        },
      };
  }
}

/**
 * Generic connect/disconnect/status surface for any integration in the
 * `redo/integration` framework (e.g. Judge.me, Yotpo). The host app owns the
 * shared UnifiedConnectionModal instance and exposes `openConnectModal` to
 * trigger it for a given IntegrationKind. The widget builder stays decoupled
 * from any specific integration's auth scheme or credential shape.
 */
export interface FrameworkIntegrationApi {
  openConnectModal: (
    kind: IntegrationKind,
    onConnectionSuccess: () => void,
  ) => void;
  disconnect: (kind: IntegrationKind) => Promise<void>;
  getStatus: (kind: IntegrationKind) => Promise<{ connected: boolean }>;
}

/**
 * Okendo predates the `redo/integration` framework and uses its own
 * Mongoose-backed integration record. It stays as a one-off until migrated.
 * The Storefront API is unauthenticated, so the only credential is the
 * subscriber (user) ID.
 */
export interface OkendoIntegrationApi {
  get: () => Promise<{ okendoUserId: string; enabled: boolean } | undefined>;
  set: (input: { okendoUserId: string }) => Promise<void>;
  disconnect: () => Promise<void>;
}

export const CheckoutWidgetsBuilderContext = createContext<{
  selectedWidget: CheckoutOptimizationWidgetType;
  setSelectedWidget: (selectedWidget: CheckoutOptimizationWidgetType) => void;
  handleSaveWidgets: (
    checkoutWidgets: CheckoutWidgetsBuilderState["checkoutWidgets"],
  ) => Promise<void>;
  onComplete: () => void;
  checkoutWidgets: CheckoutWidgetsBuilderState["checkoutWidgets"];
  setCheckoutWidgets: (
    widgets: Partial<CheckoutWidgetsBuilderState["checkoutWidgets"]>,
  ) => void;
  teamId: string;
  /**
   * The merchant team's default currency. Used by discount editors to store
   * the currency code on AMOUNT-type upsell discounts and to render the
   * trailing currency symbol in the numeric input.
   */
  defaultCurrency: Currency;
  searchCollections: (params: {
    searchString: string;
    signal?: AbortSignal;
  }) => Promise<CollectionSearchResult[]>;
  searchProducts: (params: {
    search: string;
    signal: AbortSignal;
  }) => Promise<ModalProduct[] | undefined>;
  /**
   * Optional. When the host app provides this, the Product Reviews widget
   * settings panel can connect/disconnect/check status for any integration
   * registered in the `redo/integration` framework (Judge.me, Yotpo, …).
   */
  frameworkIntegrationApi?: FrameworkIntegrationApi;
  /**
   * Optional. One-off API for Okendo until it moves to the `redo/integration`
   * framework. Selected when the widget's provider is set to OKENDO.
   */
  okendoIntegrationApi?: OkendoIntegrationApi;
  /**
   * Optional. The merchant's myshopify domain (e.g. "example.myshopify.com").
   * Used to pre-fill the Judge.me connect modal's shop-domain field so the
   * merchant doesn't have to re-type what we already know.
   */
  teamShopifyStoreUrl?: string;
  /**
   * Whether the team has Redo's first-party Reviews enabled
   * (`team.settings.reviews.enabled`), gating the Redo provider status row.
   */
  redoReviewsEnabled?: boolean;
}>({
  selectedWidget: CheckoutOptimizationWidgetType.THRESHOLD_UPSELL,
  setSelectedWidget: () => {},
  handleSaveWidgets: () => Promise.resolve(),
  onComplete: () => {},
  checkoutWidgets: {},
  setCheckoutWidgets: () => {},
  teamId: "",
  defaultCurrency: Currency.USD,
  searchCollections: async () => [],
  searchProducts: async () => undefined,
});

export const CheckoutWidgetsBuilderContextProvider = memo(
  function CheckoutWidgetsBuilderContextProvider({
    children,
    teamId,
    handleSaveWidgets,
    navigate,
    location,
    defaultCurrency,
    initialWidgetType,
    searchCollections,
    searchProducts,
    frameworkIntegrationApi,
    okendoIntegrationApi,
    teamShopifyStoreUrl,
    redoReviewsEnabled,
  }: {
    children: React.ReactNode;
    teamId: string;
    handleSaveWidgets: (
      checkoutWidgets: CheckoutWidgetsBuilderState["checkoutWidgets"],
    ) => Promise<void>;
    navigate: NavigateFunction;
    location: Location;
    defaultCurrency: Currency;
    initialWidgetType: CheckoutOptimizationWidgetType;
    searchCollections: (params: {
      searchString: string;
      signal?: AbortSignal;
    }) => Promise<CollectionSearchResult[]>;
    searchProducts: (params: {
      search: string;
      signal: AbortSignal;
    }) => Promise<ModalProduct[] | undefined>;
    frameworkIntegrationApi?: FrameworkIntegrationApi;
    okendoIntegrationApi?: OkendoIntegrationApi;
    teamShopifyStoreUrl?: string;
    redoReviewsEnabled?: boolean;
  }) {
    const [builderState, setBuilderState] =
      useState<CheckoutWidgetsBuilderState>(() => ({
        selectedWidget: initialWidgetType,
        checkoutWidgets: getDefaultWidgetByType(
          initialWidgetType,
          defaultCurrency,
        ),
      }));

    const setSelectedWidget = useHandler(
      (selectedWidget: CheckoutOptimizationWidgetType) => {
        setBuilderState((builderState) => ({
          ...builderState,
          selectedWidget,
        }));
      },
    );

    const setCheckoutWidgets = useHandler(
      (widgets: Partial<CheckoutWidgetsBuilderState["checkoutWidgets"]>) => {
        setBuilderState((builderState) => ({
          ...builderState,
          checkoutWidgets: { ...builderState.checkoutWidgets, ...widgets },
        }));
      },
    );

    const onComplete = useHandler(() => {
      const pathSegmentToRemove = "/widgets";
      if (location.pathname.endsWith(pathSegmentToRemove)) {
        navigate(location.pathname.slice(0, -pathSegmentToRemove.length));
      } else {
        navigate("..");
      }
    });

    return (
      <CheckoutWidgetsBuilderContext.Provider
        value={{
          selectedWidget: builderState.selectedWidget,
          setSelectedWidget,
          handleSaveWidgets,
          onComplete,
          checkoutWidgets: builderState.checkoutWidgets,
          setCheckoutWidgets,
          teamId,
          defaultCurrency,
          searchCollections,
          searchProducts,
          frameworkIntegrationApi,
          okendoIntegrationApi,
          teamShopifyStoreUrl,
          redoReviewsEnabled,
        }}
      >
        {children}
      </CheckoutWidgetsBuilderContext.Provider>
    );
  },
);
